import { addDays } from "./format";

export const RANGES = [
    { value: "28d", label: "28 days", days: 28 },
    { value: "90d", label: "90 days", days: 90 },
    { value: "12m", label: "12 months", days: 364 },
];
export const DEFAULT_RANGE = "90d";

export const COMPARES = [
    { value: "previous", label: "Previous period" },
    { value: "yoy", label: "Last year" },
];
export const DEFAULT_COMPARE = "previous";

export const weekEndOf = (iso) => addDays(iso, 6);

/** All page changes detected site-wide on each day, newest first. */
export function groupChangesByDay(log) {
    const byDate = new Map();
    log.forEach((entry) => byDate.set(entry.date, [...(byDate.get(entry.date) ?? []), entry]));
    return [...byDate.entries()]
        .map(([date, entries]) => ({ date, entries, changeCount: entries.reduce((total, entry) => total + entry.urlCount, 0) }))
        .sort((a, b) => b.date.localeCompare(a.date));
}

export const isSameWindow = (a, b) => !!a && !!b && a.start === b.start && a.end === b.end;

export function sliceRange(series, range, compare) {
    const n = RANGES.find((option) => option.value === range).days;
    const offset = compare === "previous" ? n : 364;
    return {
        current: series.slice(series.length - n),
        previous: series.slice(Math.max(0, series.length - n - offset), series.length - offset),
    };
}

export function summarize(points) {
    const clicks = points.reduce((total, point) => total + point.clicks, 0);
    const impressions = points.reduce((total, point) => total + point.impressions, 0);
    const position = points.length ? points.reduce((total, point) => total + point.position, 0) / points.length : 0;
    return { clicks, impressions, ctr: impressions ? (clicks / impressions) * 100 : 0, position };
}

function aggregateWeekly(points) {
    const weeks = [];
    for (let i = 0; i < points.length; i += 7) {
        const chunk = points.slice(i, i + 7);
        const week = summarize(chunk);
        weeks.push({ date: chunk[0].date, clicks: week.clicks, impressions: week.impressions, ctr: week.ctr / 100, position: week.position });
    }
    return weeks;
}

export function metricValueOf(point, metric) {
    if (metric === "ctr") return Number((point.ctr * 100).toFixed(2));
    if (metric === "position") return Number(point.position.toFixed(1));
    return point[metric];
}

export function buildChartRows(current, previous, weekly, metric) {
    const cur = weekly ? aggregateWeekly(current) : current;
    const prev = weekly ? aggregateWeekly(previous) : previous;
    return cur.map((point, i) => ({
        date: point.date,
        current: metricValueOf(point, metric),
        previous: prev[i] ? metricValueOf(prev[i], metric) : null,
        previousDate: prev[i]?.date ?? null,
    }));
}

/** The chart's x-axis key holding the date. */
export function snapToKey(keys, iso) {
    if (!keys.length || !iso || iso < keys[0]) return null;
    let match = keys[0];
    for (const key of keys) {
        if (key <= iso) match = key;
        else break;
    }
    return match;
}

/** Daily clicks: the 7-day mean from the day on against the 7 days before, in percent. */
export function eventImpact(series, iso) {
    const index = series.findIndex((point) => point.date === iso);
    if (index < 7) return null;
    const after = series.slice(index, index + 7);
    if (!after.length) return null;
    const mean = (points) => points.reduce((total, point) => total + point.clicks, 0) / points.length;
    const before = mean(series.slice(index - 7, index));
    return ((mean(after) - before) / before) * 100;
}

/** The last 7 days of clicks against the 7 days from `baselineStart`, in percent. */
export function compareToBaseline(series, baselineStart) {
    const index = series.findIndex((point) => point.date === baselineStart);
    const baseline = series.slice(index, index + 7).reduce((total, point) => total + point.clicks, 0);
    const latest = series.slice(-7).reduce((total, point) => total + point.clicks, 0);
    return ((latest - baseline) / baseline) * 100;
}
